var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { useIntl } from 'react-intl';
import { Icon } from '@folio/stripes/components';
import { RecordType } from '../../types/api';
import { AUTHORITY_TYPE_CONFIG } from './authorityTypeConfig';
import { AUTHORITY_TYPE_ICONS } from './AuthorityTypeIcons';
var isAuthorityType = function (type) {
    return Object.values(RecordType).includes(type);
};
/**
 * The name the label shows for a type: its translation, or the raw code for a vocabulary type
 * configured without a bundled translation. CZNA-1529 — the authority list measures exactly
 * these strings to size its type column, so the two can never name a type differently.
 */
export var authorityTypeName = function (intl, type) { return intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(type), defaultMessage: type }); };
/** The compact label's name weight — what the authority list measures the names in (CZNA-1529). */
export var TYPE_NAME_FONT_WEIGHT = 600;
/**
 * One visual identity for an authority type everywhere it is named.
 *
 * The colour and pictogram are the same ones introduced by the proposal-create
 * chooser. Vocabulary registry codes are deliberately not forced into an authority
 * palette: they are runtime data, so they get a neutral tag marker and their translated
 * label (or the raw code for a newly configured type without a bundled translation).
 */
var AuthorityTypeLabel = function (_a) {
    var type = _a.type, _b = _a.size, size = _b === void 0 ? 'compact' : _b, className = _a.className, _c = _a.iconOnly, iconOnly = _c === void 0 ? false : _c, _d = _a.singleLine, singleLine = _d === void 0 ? false : _d;
    var intl = useIntl();
    var large = size === 'large';
    var diameter = large ? 44 : 28;
    var authorityType = isAuthorityType(type) ? type : null;
    var config = authorityType ? AUTHORITY_TYPE_CONFIG[authorityType] : null;
    var label = authorityTypeName(intl, type);
    return (React.createElement("span", { className: className, "data-testid": "authority-type-label-".concat(type), 
        // The marker inside is aria-hidden, so without its name the label would be announced as nothing.
        role: iconOnly ? 'img' : undefined, "aria-label": iconOnly ? label : undefined, title: iconOnly || singleLine ? label : undefined, style: {
            alignItems: 'center',
            display: 'inline-flex',
            flexDirection: large ? 'column' : 'row',
            gap: large ? '10px' : '7px',
            maxWidth: singleLine ? '100%' : undefined,
            minWidth: 0,
        } },
        React.createElement("span", { "aria-hidden": "true", style: {
                alignItems: 'center',
                background: config
                    ? "linear-gradient(135deg, ".concat(config.gradientFrom, ", ").concat(config.gradientTo, ")")
                    : 'linear-gradient(135deg, #546e7a, #90a4ae)',
                borderRadius: '50%',
                color: 'white',
                display: 'inline-flex',
                flex: "0 0 ".concat(diameter, "px"),
                height: "".concat(diameter, "px"),
                justifyContent: 'center',
                overflow: 'hidden',
                width: "".concat(diameter, "px"),
            } },
            React.createElement("span", { style: { display: 'inline-flex', transform: large ? undefined : 'scale(0.68)' } }, authorityType ? AUTHORITY_TYPE_ICONS[authorityType] : React.createElement(Icon, { icon: "tag", size: "medium" }))),
        !iconOnly && (React.createElement("span", { style: __assign({ color: '#333', fontSize: large ? '15px' : undefined, fontWeight: large ? 700 : TYPE_NAME_FONT_WEIGHT }, (singleLine ? { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {})) }, label))));
};
export default AuthorityTypeLabel;
